<div
  class="ptl-data-table"
  [class.ptl-data-table--dark-header]="darkHeader"
  [class.ptl-data-table--fit-viewport]="fitToViewport"
  [class.ptl-data-table--scroll-layout]="layout === 'scroll'"
>
  <div class="ptl-data-table__toolbar">
    <ng-content select="[table-toolbar]"></ng-content>
  </div>

  @if (showsTable) {
    <!--
      The rows are wrapped so the busy veil can cover them alone: the toolbar above it stays live, which is
      what lets a reader keep narrowing while the last narrowing is still being fetched.
    -->
    <div class="ptl-data-table__rows">
      <div class="ptl-data-table__scroll" #scrollArea>
        <div
          class="ptl-data-table__table"
          role="table"
          [attr.aria-label]="tableAriaLabel || null"
          [attr.aria-busy]="loading ? 'true' : null"
        >
          <div class="ptl-data-table__header" role="row">
            @if (selectable) {
              <div
                class="ptl-data-table__cell ptl-data-table__cell--head ptl-data-table__cell--select"
                role="columnheader"
                [attr.aria-label]="effectiveSelectAllAriaLabel"
                [class.ptl-data-table__cell--sticky]="selectSticky"
                [style.inset-inline-start]="selectSticky ? '0' : null"
                [style.width]="selectColumnWidth"
              >
                <ptl-sg-checkbox
                  size="s"
                  [checked]="allRowsSelected"
                  [disabled]="loading"
                  [ariaLabel]="effectiveSelectAllAriaLabel"
                  (checkboxChange)="onSelectAllToggle($event)"
                ></ptl-sg-checkbox>
              </div>
            }
            @for (col of columns; track col.key) {
              <div
                class="ptl-data-table__cell ptl-data-table__cell--head"
                role="columnheader"
                [attr.aria-label]="!col.label ? col.ariaLabel || col.key : null"
                [style.text-align]="cellAlign(col)"
                [class.ptl-data-table__cell--numeric]="col.numeric"
                [class.ptl-data-table__cell--sticky]="isSticky($index)"
                [style.inset-inline-start]="stickyOffset($index)"
                [style.width]="columnWidth(col)"
              >
                @if (col.sortable) {
                  <button type="button" class="ptl-data-table__sort" [disabled]="loading" (click)="onSort(col)">
                    <ng-container *ngTemplateOutlet="headerBody; context: { $implicit: col }"></ng-container>
                    <ptl-icon
                      class="ptl-data-table__sort-icon"
                      [class.is-active]="sortKey === col.key"
                      [class.is-desc]="col.sortDirectional !== false && sortKey === col.key && sortDir === 'desc'"
                      [icon]="col.sortDirectional === false ? LucideChevronsUpDown : LucideArrowDown"
                      [size]="'xs'"
                    ></ptl-icon>
                  </button>
                } @else {
                  <ng-container *ngTemplateOutlet="headerBody; context: { $implicit: col }"></ng-container>
                }
              </div>
            }
          </div>

          @if (showsSkeleton) {
            <div class="ptl-data-table__body" role="rowgroup" aria-hidden="true">
              @for (skeletonRow of skeletonRowList; track $index) {
                <div class="ptl-data-table__row ptl-data-table__row--skeleton" role="row">
                  @if (selectable) {
                    <div
                      class="ptl-data-table__cell ptl-data-table__cell--select"
                      role="cell"
                      [class.ptl-data-table__cell--sticky]="selectSticky"
                      [style.inset-inline-start]="selectSticky ? '0' : null"
                    >
                      <span class="ptl-data-table__skeleton ptl-data-table__skeleton--box"></span>
                    </div>
                  }
                  @for (col of columns; track col.key) {
                    <div
                      class="ptl-data-table__cell"
                      role="cell"
                      [style.text-align]="cellAlign(col)"
                      [class.ptl-data-table__cell--numeric]="col.numeric"
                      [class.ptl-data-table__cell--sticky]="isSticky($index)"
                      [style.inset-inline-start]="stickyOffset($index)"
                      [style.width]="columnWidth(col)"
                    >
                      <span class="ptl-data-table__skeleton" [style.width]="skeletonBarWidth(skeletonRow, $index)"></span>
                    </div>
                  }
                </div>
              }
            </div>
          } @else {
            <div class="ptl-data-table__body" role="rowgroup">
              @for (row of pagedRows; track trackRow($index, row)) {
                <div
                  class="ptl-data-table__row"
                  role="row"
                  [class.ptl-data-table__row--clickable]="rowClickable"
                  [attr.tabindex]="rowClickable ? 0 : null"
                  [attr.aria-label]="rowAriaLabel ? rowAriaLabel(row) : null"
                  (click)="onRowClick(row)"
                  (keydown.enter)="onRowClick(row)"
                >
                  @if (selectable) {
                    <div
                      class="ptl-data-table__cell ptl-data-table__cell--select"
                      role="cell"
                      [class.ptl-data-table__cell--sticky]="selectSticky"
                      [style.inset-inline-start]="selectSticky ? '0' : null"
                      (click)="$event.stopPropagation()"
                    >
                      <ptl-sg-checkbox
                        size="s"
                        [checked]="isRowSelected(row)"
                        [ariaLabel]="resolveRowSelectAriaLabel(row)"
                        (checkboxChange)="onRowSelectionToggle(row, $event)"
                      ></ptl-sg-checkbox>
                    </div>
                  }
                  @for (col of columns; track col.key) {
                    <div
                      class="ptl-data-table__cell"
                      role="cell"
                      [attr.dir]="cellDir(col)"
                      [style.text-align]="cellAlign(col)"
                      [class.ptl-data-table__cell--numeric]="col.numeric"
                      [class.ptl-data-table__cell--sticky]="isSticky($index)"
                      [style.inset-inline-start]="stickyOffset($index)"
                      [style.width]="columnWidth(col)"
                    >
                      @if (col.truncate) {
                        <span class="ptl-data-table__truncate" [attr.title]="truncateTitle(row, col)">
                          <ng-container *ngTemplateOutlet="cellBody; context: { $implicit: row, col: col }"></ng-container>
                        </span>
                      } @else {
                        <ng-container *ngTemplateOutlet="cellBody; context: { $implicit: row, col: col }"></ng-container>
                      }
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      </div>

      <ptl-busy-overlay [active]="showsBusyVeil" [spinner]="true" [label]="busyLabel"></ptl-busy-overlay>
    </div>

    @if (pageSize && total) {
      <ptl-pagination-footer
        class="ptl-data-table__footer"
        [totalItems]="total"
        [pageSize]="pageSize"
        [currentPage]="displayPage + 1"
        [pageSizeOptions]="footerPageSizeOptions"
        [disabled]="loading"
        (pageChange)="onPage($event)"
        (pageSizeChange)="onPageSize($event)"
      ></ptl-pagination-footer>
    }
  } @else {
    <div class="ptl-data-table__empty">
      <ng-content select="[table-empty]"></ng-content>
    </div>
  }
</div>

<ng-template #cellBody let-row let-col="col">
  @if (cellTemplate(col); as tpl) {
    <ng-container *ngTemplateOutlet="tpl; context: { $implicit: row, row: row, column: col }"></ng-container>
  } @else {
    {{ row[col.key] }}
  }
</ng-template>

<ng-template #headerBody let-col>
  @if (headerTemplate(col); as tpl) {
    <ng-container *ngTemplateOutlet="tpl; context: { $implicit: col, column: col }"></ng-container>
  } @else {
    {{ col.label }}
  }
</ng-template>
